import { v4 as uuid } from 'uuid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { imageUrl } from 'app/frontend/base/helpers'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import CastNotePopover from './CastNotePopover'
import useCastNoteDraft from '../hooks/useCastNoteDraft'
import useObservedWidth from '../hooks/useObservedWidth'
import useTimelineTicks from '../hooks/useTimelineTicks'
import useVisibleTimelineTicks from '../hooks/useVisibleTimelineTicks'
import { formatTimelineTimestamp } from './timelineMath'
import { NOTE_ICON_URL } from '../config/generalAbilities'
import NoteTooltipContent from './NoteTooltipContent'
import TimelineHoverCastPreview from './TimelineHoverCastPreview'
import useTimelineZoomInteraction from '../hooks/useTimelineZoomInteraction'
import TimelineDragSelectionOverlay from './TimelineDragSelectionOverlay'

type ActorTimelineCast = {
  id: string
  spellId: number | null
  at: number
  duration: number
  cooldown: number
  color: string
  issueType: 'none' | 'warning' | 'error'
  issueMessage?: string
  note?: string
}

type ActorTimelineRow = {
  key: string
  spellId: number | null
  casts: ActorTimelineCast[]
}

type TimelineStorageTarget =
  | { type: 'actor'; actorIndex: number }
  | { type: 'general' }

type ActorTimelineContainerProps = {
  storageTarget: TimelineStorageTarget
  rows: ActorTimelineRow[]
  canEditCasts?: boolean
  hoverTime?: number | null
  onHoverTimeChange?: (nextHoverTime: number) => void
  onHoveredRowSpellIdChange?: (spellId: number | null) => void
  onCastNotePopoverOpenChange?: (open: boolean) => void
  className?: string
}

const ROW_HEIGHT = 20
const ROW_GAP = 0
const ICON_SIZE = 20
const ICON_INSET = 1
const MIN_ZOOM_SELECTION_DURATION = 1
const ActorTimelineContainer = ({
  storageTarget,
  rows,
  canEditCasts,
  hoverTime,
  onHoverTimeChange,
  onHoveredRowSpellIdChange,
  onCastNotePopoverOpenChange,
  className,
}: ActorTimelineContainerProps) => {
  const { canEdit, computed, updateTimeline, zoom, setZoom } =
    useTeamPlanTimeline()
  const isCastEditingEnabled = canEdit && canEditCasts !== false
  const { maxTime, phaseDividers, clamp } = computed
  const rootRef = useRef<HTMLDivElement>(null)
  const timelineWidth = useObservedWidth(rootRef)
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null)
  const [hoveredCastId, setHoveredCastId] = useState<string | null>(null)
  const [suppressPreviewUntilMove, setSuppressPreviewUntilMove] =
    useState(false)
  const ignoreClickCastIdRef = useRef<string | null>(null)
  const [editingCastId, setEditingCastId] = useState<string | null>(null)
  const [castPopoverOpen, setCastPopoverOpen] = useState(false)
  const {
    draft: castNoteDraft,
    setDraft: setCastNoteDraft,
    textareaRef: castNoteTextareaRef,
    insertMarker: insertMarkerToNote,
  } = useCastNoteDraft()

  useEffect(() => {
    if (!hoveredCastId) return
    const hasHoveredCast = rows.some((row) =>
      row.casts.some((cast) => cast.id === hoveredCastId),
    )
    if (!hasHoveredCast) {
      setHoveredCastId(null)
    }
  }, [hoveredCastId, rows])

  useEffect(() => {
    if (!editingCastId) return
    const hasEditingCast = rows.some((row) =>
      row.casts.some((cast) => cast.id === editingCastId),
    )
    if (hasEditingCast) return
    setEditingCastId(null)
    setCastPopoverOpen(false)
    setCastNoteDraft('')
  }, [editingCastId, rows])

  const { minorTickTimes, minuteTickTimes } = useTimelineTicks({
    maxTime,
    phaseDividers,
  })

  const visibleRange = useMemo(() => {
    const start = clamp(zoom?.start ?? 0, 0, maxTime)
    const end = clamp(zoom?.end ?? maxTime, start + 1, maxTime)
    return { start, end }
  }, [clamp, maxTime, zoom?.end, zoom?.start])
  const isZoomed = visibleRange.start > 0 || visibleRange.end < maxTime
  const visibleDuration = Math.max(visibleRange.end - visibleRange.start, 1)
  const rowsHeight =
    rows.length * ROW_HEIGHT + Math.max(rows.length - 1, 0) * ROW_GAP
  const rowsTop = 0
  const svgHeight = rowsTop + rowsHeight

  const onDragZoom = useCallback(
    (range: { start: number; end: number }) => {
      const start = Number(clamp(range.start, 0, maxTime).toFixed(2))
      const end = Number(clamp(range.end, start + 1, maxTime).toFixed(2))
      if (end - start < MIN_ZOOM_SELECTION_DURATION) return
      if (start <= 0 && end >= maxTime) {
        setZoom(null)
        return
      }
      setZoom({ start, end })
    },
    [clamp, maxTime, setZoom],
  )

  const {
    toTime,
    toX,
    selectionPixels,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    lastDragFinishedAtRef,
  } = useTimelineZoomInteraction({
    maxTime,
    visibleRange,
    timelineWidth,
    minSelectionDuration: MIN_ZOOM_SELECTION_DURATION,
    disabled: isZoomed,
    onZoom: onDragZoom,
  })

  const toWidth = useCallback(
    (duration: number) =>
      Math.max((Math.max(duration, 1) / visibleDuration) * timelineWidth, 1),
    [timelineWidth, visibleDuration],
  )

  const { visibleMinorTickTimes, visibleMinuteTickTimes } =
    useVisibleTimelineTicks({
      minorTickTimes,
      minuteTickTimes,
      visibleRange,
    })

  const updateStoredCasts = useCallback(
    (
      updater: (
        casts: {
          id: string
          spell_id: number | null
          at: number
          note?: string
        }[],
      ) => {
        id: string
        spell_id: number | null
        at: number
        note?: string
      }[],
      pushHistory = true,
    ) => {
      if (!isCastEditingEnabled) return
      updateTimeline((next) => {
        if (storageTarget.type === 'actor') {
          const actor = next.actors[storageTarget.actorIndex]
          if (!actor) return
          actor.casts = updater(actor.casts)
          return
        }
        if (!next.general) next.general = { casts: [] }
        next.general.casts = updater(next.general.casts)
      }, pushHistory)
    },
    [isCastEditingEnabled, storageTarget, updateTimeline],
  )

  const addCast = useCallback(
    (rowIndex: number, at: number) => {
      const row = rows[rowIndex]
      if (!row) return
      const castId = uuid()
      updateStoredCasts((casts) => [
        ...casts,
        {
          id: castId,
          spell_id: row.spellId,
          at: Number(clamp(at, 0, maxTime).toFixed(2)),
        },
      ])
      if (row.spellId === null) {
        setEditingCastId(castId)
        setCastNoteDraft('')
        setCastPopoverOpen(true)
      }
    },
    [clamp, maxTime, rows, updateStoredCasts],
  )

  const removeCast = useCallback(
    (castId: string) => {
      setSuppressPreviewUntilMove(true)
      updateStoredCasts((casts) => casts.filter((cast) => cast.id !== castId))
    },
    [updateStoredCasts],
  )

  const onCastPointerDown = useCallback(
    (
      event: React.PointerEvent<SVGRectElement>,
      rowWidth: number,
      castId: string,
    ) => {
      event.preventDefault()
      event.stopPropagation()
      const cast = rows
        .flatMap((row) => row.casts)
        .find((entry) => entry.id === castId)
      if (!cast) return

      const startX = event.clientX
      const initialAt = cast.at
      let moved = false

      const onMove = (moveEvent: PointerEvent) => {
        const delta = moveEvent.clientX - startX
        const deltaTime = (delta / rowWidth) * visibleDuration
        const nextAt = Number(
          clamp(initialAt + deltaTime, 0, maxTime).toFixed(2),
        )
        if (nextAt !== initialAt) moved = true
        onHoverTimeChange?.(nextAt)
        updateStoredCasts(
          (casts) =>
            casts.map((entry) =>
              entry.id === castId ? { ...entry, at: nextAt } : entry,
            ),
          false,
        )
      }

      const onUp = () => {
        if (moved) ignoreClickCastIdRef.current = castId
        updateTimeline(() => null, true)
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [
      clamp,
      maxTime,
      onHoverTimeChange,
      rows,
      updateStoredCasts,
      updateTimeline,
      visibleDuration,
    ],
  )

  const openCastNoteEditor = useCallback((cast: ActorTimelineCast) => {
    setEditingCastId(cast.id)
    setCastNoteDraft(cast.note ?? '')
    setCastPopoverOpen(true)
  }, [])

  const saveCastNote = useCallback(() => {
    if (!editingCastId) return
    const nextNote = castNoteDraft.trim()
    const current = rows
      .flatMap((row) => row.casts)
      .find((cast) => cast.id === editingCastId)
    if (!current || (current.note ?? '') === nextNote) return
    updateStoredCasts((casts) =>
      casts.map((cast) =>
        cast.id === editingCastId
          ? { ...cast, note: nextNote.length ? nextNote : undefined }
          : cast,
      ),
    )
  }, [castNoteDraft, editingCastId, rows, updateStoredCasts])

  const activeCastPosition = useMemo(() => {
    if (!editingCastId) return null
    const rowIndex = rows.findIndex((row) =>
      row.casts.some((cast) => cast.id === editingCastId),
    )
    if (rowIndex < 0) return null
    const cast = rows[rowIndex].casts.find(
      (entry) => entry.id === editingCastId,
    )
    if (!cast) return null
    const y = rowsTop + rowIndex * (ROW_HEIGHT + ROW_GAP) + ROW_HEIGHT / 2
    return { x: toX(cast.at), y }
  }, [editingCastId, rows, rowsTop, toX])

  useEffect(() => {
    onCastNotePopoverOpenChange?.(castPopoverOpen)
    return () => onCastNotePopoverOpenChange?.(false)
  }, [castPopoverOpen, onCastNotePopoverOpenChange])

  return (
    <div
      className='w-full'
      ref={rootRef}
      onMouseLeave={() => {
        setHoveredRowIndex(null)
        setHoveredCastId(null)
        setSuppressPreviewUntilMove(false)
        onHoveredRowSpellIdChange?.(null)
      }}
    >
      <svg
        className='block w-full'
        width='100%'
        height={svgHeight}
        viewBox={`0 0 ${timelineWidth} ${svgHeight}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <g className={className}>
          <rect
            x={0}
            y={rowsTop}
            width={timelineWidth}
            height={rowsHeight}
            fill='currentColor'
            rx={4}
            ry={4}
          />
        </g>
        <g className='text-border'>
          <rect
            x={0}
            y={rowsTop}
            width={timelineWidth}
            height={rowsHeight}
            fill='transparent'
            stroke='currentColor'
            strokeWidth={1.25}
            vectorEffect='non-scaling-stroke'
            rx={8}
            ry={8}
          />
        </g>

        <g className='text-border'>
          {visibleMinorTickTimes.map((tickAt) => {
            const x = toX(tickAt)
            return (
              <line
                key={`minor-tick-${tickAt}`}
                x1={x}
                x2={x}
                y1={rowsTop}
                y2={rowsTop + rowsHeight}
                stroke='currentColor'
                strokeWidth={1}
                opacity={0.45}
              />
            )
          })}
        </g>

        <g className='text-foreground'>
          {visibleMinuteTickTimes.map((tickAt) => {
            const x = toX(tickAt)
            return (
              <line
                key={`minute-tick-${tickAt}`}
                x1={x}
                x2={x}
                y1={rowsTop}
                y2={rowsTop + rowsHeight}
                stroke='currentColor'
                strokeWidth={1}
                opacity={0.3}
              />
            )
          })}
        </g>

        {rows.map((row, rowIndex) => {
          const y = rowsTop + rowIndex * (ROW_HEIGHT + ROW_GAP)
          const isHoveredRow = hoveredRowIndex === rowIndex
          return (
            <g key={row.key}>
              {isHoveredRow && (
                <rect
                  x={0}
                  y={y}
                  width={timelineWidth}
                  height={ROW_HEIGHT}
                  fill='var(--color-accent)'
                  opacity={0.22}
                  pointerEvents='none'
                />
              )}
              <rect
                x={0}
                y={y}
                width={timelineWidth}
                height={ROW_HEIGHT}
                fill='transparent'
                onMouseMove={() => {
                  setHoveredRowIndex(rowIndex)
                  setSuppressPreviewUntilMove(false)
                  onHoveredRowSpellIdChange?.(row.spellId)
                }}
                onClick={(event) => {
                  if (!isCastEditingEnabled) return
                  if (Date.now() - lastDragFinishedAtRef.current < 200) return
                  const svg = event.currentTarget.ownerSVGElement
                  if (!svg) return
                  const rect = svg.getBoundingClientRect()
                  const at = toTime(event.clientX - rect.left)
                  addCast(rowIndex, Number(at.toFixed(2)))
                }}
              />

              {row.casts.map((cast) => {
                const castStart = cast.at
                const castDurationEnd = cast.at + Math.max(cast.duration, 0)
                const castCooldownEnd = cast.at + Math.max(cast.cooldown, 0)
                const label = formatTimelineTimestamp(cast.at)
                const isCastStartVisible =
                  castStart >= visibleRange.start &&
                  castStart <= visibleRange.end
                const durationVisibleStart = Math.max(
                  castStart,
                  visibleRange.start,
                )
                const durationVisibleEnd = Math.min(
                  castDurationEnd,
                  visibleRange.end,
                )
                const cooldownVisibleStart = Math.max(
                  castDurationEnd,
                  visibleRange.start,
                )
                const cooldownVisibleEnd = Math.min(
                  castCooldownEnd,
                  visibleRange.end,
                )
                const durationVisibleWidth =
                  durationVisibleEnd > durationVisibleStart
                    ? toWidth(durationVisibleEnd - durationVisibleStart)
                    : 0
                const cooldownVisibleWidth =
                  cooldownVisibleEnd > cooldownVisibleStart
                    ? toWidth(cooldownVisibleEnd - cooldownVisibleStart)
                    : 0
                const castBorderColor = (() => {
                  if (cast.issueType === 'error') {
                    return 'var(--color-destructive-foreground)'
                  }
                  if (cast.issueType === 'warning') {
                    return 'var(--color-warning-foreground)'
                  }
                  return 'none'
                })()
                const hasCastBorder = castBorderColor !== 'none'
                const hasOverlap = cast.issueType !== 'none'
                const noteText = cast.note?.trim() ?? ''
                const hasNote = Boolean(noteText)
                const shouldShowTooltip =
                  Boolean(cast.issueMessage) || hasNote || isCastEditingEnabled
                const castIconHitbox = (
                  <rect
                    x={toX(cast.at)}
                    y={y}
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    fill='transparent'
                    stroke={castBorderColor}
                    strokeWidth={hasCastBorder ? 2 : 0}
                    rx={2}
                    ry={2}
                    className={isCastEditingEnabled ? 'cursor-grab' : ''}
                    onMouseEnter={() => setHoveredCastId(cast.id)}
                    onMouseLeave={() => setHoveredCastId(null)}
                    onPointerDown={(event) => {
                      if (!isCastEditingEnabled) return
                      event.stopPropagation()
                      onCastPointerDown(event, timelineWidth, cast.id)
                    }}
                    onContextMenu={(event) => {
                      if (!isCastEditingEnabled) return
                      event.preventDefault()
                      event.stopPropagation()
                      removeCast(cast.id)
                    }}
                    onClick={(event) => {
                      if (!isCastEditingEnabled) return
                      event.stopPropagation()
                      if (ignoreClickCastIdRef.current === cast.id) {
                        ignoreClickCastIdRef.current = null
                        return
                      }
                      if (castPopoverOpen && editingCastId === cast.id) {
                        saveCastNote()
                        setEditingCastId(null)
                        setCastPopoverOpen(false)
                        return
                      }
                      openCastNoteEditor(cast)
                    }}
                  />
                )
                return (
                  <g key={cast.id}>
                    {durationVisibleWidth > 0 && (
                      <rect
                        x={toX(durationVisibleStart)}
                        y={y + 1}
                        width={durationVisibleWidth}
                        height={ROW_HEIGHT - 2}
                        fill={cast.color}
                        fillOpacity={0.7}
                        pointerEvents='none'
                      />
                    )}
                    {cooldownVisibleWidth > 0 && (
                      <rect
                        x={toX(cooldownVisibleStart)}
                        y={y + 1}
                        width={cooldownVisibleWidth}
                        height={ROW_HEIGHT - 2}
                        fill={cast.color}
                        fillOpacity={0.3}
                        pointerEvents='none'
                      />
                    )}

                    {isCastStartVisible && (
                      <>
                        <image
                          x={toX(cast.at) + (hasCastBorder ? ICON_INSET : 0)}
                          y={y + (hasCastBorder ? ICON_INSET : 0)}
                          width={
                            ICON_SIZE - (hasCastBorder ? ICON_INSET * 2 : 0)
                          }
                          height={
                            ICON_SIZE - (hasCastBorder ? ICON_INSET * 2 : 0)
                          }
                          href={
                            cast.spellId === null
                              ? NOTE_ICON_URL
                              : imageUrl(`icons/${cast.spellId}.jpg`)
                          }
                          preserveAspectRatio='xMidYMid slice'
                          style={
                            hasOverlap ? { filter: 'grayscale(1)' } : undefined
                          }
                          pointerEvents='none'
                        />
                        {shouldShowTooltip ? (
                          <Tooltip disableHoverableContent>
                            <TooltipTrigger asChild>
                              {castIconHitbox}
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className='max-w-72 space-y-1 text-center'>
                                {cast.issueMessage ? (
                                  <div
                                    className={
                                      cast.issueType === 'error'
                                        ? 'text-destructive-foreground'
                                        : cast.issueType === 'warning'
                                        ? 'text-warning-foreground'
                                        : ''
                                    }
                                  >
                                    {cast.issueMessage}
                                  </div>
                                ) : null}
                                {hasNote ? (
                                  <>
                                    <NoteTooltipContent text={noteText} />
                                    {isCastEditingEnabled ? (
                                      <div className='text-xs text-muted-foreground'>
                                        Right click to remove cast
                                      </div>
                                    ) : null}
                                  </>
                                ) : isCastEditingEnabled ? (
                                  <div className='text-xs text-muted-foreground'>
                                    Click to add message, right click to remove
                                    cast
                                  </div>
                                ) : null}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          castIconHitbox
                        )}
                        <g className='pointer-events-none'>
                          <text
                            x={toX(cast.at) + ICON_SIZE + 3}
                            y={y + 1 + ROW_HEIGHT / 2}
                            fill={'var(--color-accent-foreground)'}
                            fontSize={12}
                            dominantBaseline='middle'
                          >
                            {label}
                          </text>
                        </g>
                      </>
                    )}
                  </g>
                )
              })}
            </g>
          )
        })}

        {selectionPixels && (
          <TimelineDragSelectionOverlay
            startX={selectionPixels.startX}
            endX={selectionPixels.endX}
            top={rowsTop}
            height={rowsHeight}
          />
        )}

        {!castPopoverOpen && hoverTime !== null && hoverTime !== undefined && (
          <g className='text-accent-foreground pointer-events-none'>
            <line
              x1={toX(hoverTime)}
              x2={toX(hoverTime)}
              y1={rowsTop}
              y2={rowsTop + rowsHeight}
              stroke='currentColor'
              strokeWidth={1}
            />
          </g>
        )}

        {!castPopoverOpen &&
          isCastEditingEnabled &&
          hoverTime !== null &&
          hoverTime !== undefined &&
          hoveredRowIndex !== null &&
          hoveredCastId === null &&
          !suppressPreviewUntilMove &&
          rows[hoveredRowIndex] && (
            <TimelineHoverCastPreview
              hoverTime={hoverTime}
              hoveredRowIndex={hoveredRowIndex}
              rowsTop={rowsTop}
              rowHeight={ROW_HEIGHT}
              rowGap={ROW_GAP}
              iconSize={ICON_SIZE}
              iconInset={ICON_INSET}
              toX={toX}
              getRowSpellId={(rowIndex) => rows[rowIndex]?.spellId}
            />
          )}
      </svg>
      {isCastEditingEnabled && activeCastPosition && (
        <CastNotePopover
          open={castPopoverOpen}
          position={activeCastPosition}
          draft={castNoteDraft}
          textareaRef={castNoteTextareaRef}
          onDraftChange={setCastNoteDraft}
          onOpenChange={(open) => {
            if (!open) {
              saveCastNote()
              setEditingCastId(null)
            }
            setCastPopoverOpen(open)
          }}
          onInsertMarker={insertMarkerToNote}
        />
      )}
    </div>
  )
}

export type { ActorTimelineRow }
export default ActorTimelineContainer
